import { createTheme, responsiveFontSizes, type ThemeOptions } from '@mui/material/styles';

/**
 * Shape used by status-badge-style components (bg/border/dot/text), which
 * doesn't fit MUI's standard {main,light,dark,contrastText} palette shape.
 */
interface StatusPaletteColor {
  bg: string;
  border: string;
  dot: string;
  text: string;
}

interface SlatePaletteScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  800: string;
  900: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    status: {
      success: StatusPaletteColor;
      warning: StatusPaletteColor;
      error: StatusPaletteColor;
      info: StatusPaletteColor;
    };
    /**
     * A second gray family (Tailwind's "slate") used interchangeably with
     * `grey` (Tailwind's "gray") across the app's existing designs.
     */
    slate: SlatePaletteScale;
  }
  interface PaletteOptions {
    status?: {
      success?: StatusPaletteColor;
      warning?: StatusPaletteColor;
      error?: StatusPaletteColor;
      info?: StatusPaletteColor;
    };
    slate?: Partial<SlatePaletteScale>;
  }
}

const baseThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700 },
    h2: { fontSize: '2rem', fontWeight: 700 },
    h3: { fontSize: '1.75rem', fontWeight: 600 },
    h4: { fontSize: '1.5rem', fontWeight: 600 },
    h5: { fontSize: '1.25rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.75rem' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
      defaultProps: { disableElevation: true },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { border: '1px solid', borderColor: 'divider' },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small', fullWidth: true },
    },
    MuiTableCell: {
      styleOverrides: {
        head: { fontWeight: 700 },
      },
    },
    // Keep the page scrollable (and the scrollbar visible) while any dropdown
    // overlay is open. Dialogs (MuiModal/MuiDialog) intentionally keep their lock.
    MuiPopover: {
      defaultProps: { disableScrollLock: true },
    },
    MuiMenu: {
      defaultProps: { disableScrollLock: true },
    },
  },
};

export const theme = responsiveFontSizes(createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'light',
    primary: { main: '#0073ea', light: '#4da3ff', dark: '#005dc0' },
    secondary: { main: '#9c27b0', light: '#ba68c8', dark: '#7b1fa2' },
    background: { default: '#f5f5f5', paper: '#ffffff' },
    // Corrected to the colors actually used across the app (the previous
    // values were MUI-default-ish and never matched any rendered UI).
    error: { main: '#f04438' },
    warning: { main: '#d97706' },
    info: { main: '#0288d1' },
    success: { main: '#0ca678' },
    divider: '#e5e7eb',
    // Overridden to the app's real gray scale (used throughout for text,
    // borders, and subtle backgrounds) instead of MUI's default grey stops.
    grey: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#45556c',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    // A second gray family (Tailwind "slate") used alongside `grey`
    // ("gray") throughout the app's existing designs — not a duplicate,
    // the two scales are visually distinct and both appear in real UI.
    slate: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      800: '#1e293b',
      900: '#0f172a',
    },
    text: {
      primary: '#111827',
      secondary: '#6b7280',
    },
    // Status-badge color sets (bg/border/dot/text) — sourced from the shared
    // StatusBadge component's existing STATUS_CFG values.
    status: {
      success: { bg: '#ecfdf5', border: '#12b76a', dot: '#0ca678', text: '#0ca678' },
      warning: { bg: '#fffbeb', border: '#f79009', dot: '#f79009', text: '#e17100' },
      error:   { bg: '#fef2f2', border: '#f04438', dot: '#f04438', text: '#d92d20' },
      info:    { bg: '#eff6ff', border: '#0073ea', dot: '#0073ea', text: '#0356b6' },
    },
  },
}));
